/**
 * _shared.jsx — Small presentational primitives shared across the
 * Insights tab (badge, stat tile, format helpers).
 */
import React from 'react';
import { formatDistanceToNow, parseISO } from 'date-fns';

/**
 * Engagement-score badge — renders a small pill colored by tier
 * (hot/warm/cool/cold) with the 0–100 score. Tooltip shows the
 * three component points so the formula stays auditable in-place.
 */
const TIER_STYLES = {
  hot:  { bg: 'bg-rose-100',   text: 'text-rose-700',   ring: 'ring-rose-200',   icon: '🔥', label: 'Hot' },
  warm: { bg: 'bg-amber-100',  text: 'text-amber-700',  ring: 'ring-amber-200',  icon: '✨', label: 'Warm' },
  cool: { bg: 'bg-sky-100',    text: 'text-sky-700',    ring: 'ring-sky-200',    icon: '💤', label: 'Cool' },
  cold: { bg: 'bg-slate-100',  text: 'text-slate-500',  ring: 'ring-slate-200',  icon: '🚫', label: 'Cold' },
};

export const EngagementBadge = ({ engagement, size = 'sm', testId }) => {
  if (!engagement) return null;
  const t = TIER_STYLES[engagement.tier] || TIER_STYLES.cold;
  const c = engagement.components || {};
  const tooltip = `${t.label} (${engagement.score}/100) · recency ${c.recency_pts ?? 0} · velocity ${c.velocity_pts ?? 0} · cadence ${c.cadence_pts ?? 0}`;
  const sizeCls = size === 'lg'
    ? 'text-xs py-1 px-2.5 gap-1'
    : 'text-[10px] py-0.5 px-1.5 gap-0.5';
  return (
    <span
      title={tooltip}
      className={`inline-flex items-center font-semibold rounded-full ring-1 ${t.bg} ${t.text} ${t.ring} ${sizeCls}`}
      data-testid={testId}
      data-engagement-tier={engagement.tier}
    >
      <span aria-hidden="true">{t.icon}</span>
      <span className="tabular-nums">{engagement.score}</span>
    </span>
  );
};

export const InsightStat = ({ label, value, highlight, testId }) => (
  <div
    className={`rounded-lg border p-3 ${highlight ? 'border-fuchsia-200 bg-fuchsia-50/40' : 'border-slate-100 bg-white'}`}
    data-testid={testId}
  >
    <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
    <div className={`mt-0.5 text-lg font-bold tabular-nums ${highlight ? 'text-fuchsia-700' : 'text-slate-800'}`}>{value}</div>
  </div>
);

export const FunnelStat = ({ label, value, hint, highlight, testId }) => (
  <div
    className={`rounded-md border p-2 ${highlight ? 'border-rose-300 bg-white' : 'border-rose-100 bg-white/60'}`}
    data-testid={testId}
  >
    <div className="text-[9px] font-semibold uppercase tracking-wider text-rose-600">{label}</div>
    <div className={`mt-0.5 text-base font-bold tabular-nums ${highlight ? 'text-rose-700' : 'text-slate-800'}`}>{value}</div>
    {hint && <div className="text-[9px] text-rose-600/70 mt-0.5">{hint}</div>}
  </div>
);

export const formatRelative = (ts) => {
  if (!ts) return '—';
  try {
    return formatDistanceToNow(typeof ts === 'string' ? parseISO(ts) : new Date(ts), { addSuffix: true });
  } catch (_e) {
    return '—';
  }
};

export const formatDuration = (sec) => {
  if (sec == null) return '—';
  if (sec < 60) return `${sec}s`;
  if (sec < 3600) return `${Math.round(sec / 60)}m`;
  if (sec < 86400) return `${(sec / 3600).toFixed(1)}h`;
  return `${(sec / 86400).toFixed(1)}d`;
};
